import { StrictMode, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { AuthProvider, useUser } from '@lib/firebase/provider'
import { Center, ChakraProvider } from '@chakra-ui/react'
import { store, persistor } from '@lib/store'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { setOrgId } from '@features/organisations/slice'
import { parseSearch, stringifySearch } from '@lib/routerSearch'
import { createListStateService } from '@lib/listState/service'
import { routeTree } from './routeTree.gen'
import theme from './theme'

const setOrg = (orgId: string) => {
  store.dispatch(setOrgId(orgId))
}
const listState = createListStateService()

export const router = createRouter({
  routeTree,
  context: {
    auth: null,
    setOrg,
    getTitle: () => 'Cloudmix',
    listState,
  },
  parseSearch,
  stringifySearch,
  defaultNotFoundComponent: () => (
    <Center h="100vh">The page you are looking for does not exist</Center>
  ),
})

// Prevent Sentry from initializing in development
if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: 'https://03e809ffddd34708d679c9f1a17bb84d@o229164.ingest.sentry.io/4505989380505600',
    environment: import.meta.env.VERCEL_ENV ?? 'production',

    ignoreErrors: [
      'ResizeObserver loop limit exceeded', // Prevent Vercel Toolbar error
    ],

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: 0.1,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    replaysOnErrorSampleRate: 1.0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const App = () => {
  const auth = useUser()
  const { loading, user } = auth

  const authContext = useMemo(
    () => ({
      loading,
      user,
    }),
    [loading, user]
  )

  useEffect(() => {
    router.invalidate()
  }, [authContext])

  return (
    <RouterProvider router={router} context={{ auth, setOrg, listState }} />
  )
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <AuthProvider>
        <Provider store={store}>
          <PersistGate persistor={persistor} loading={null}>
            <ChakraProvider resetCSS theme={theme} portalZIndex={100}>
              <App />
            </ChakraProvider>
          </PersistGate>
        </Provider>
      </AuthProvider>
    </StrictMode>
  )
}
